import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MarketingDailyEntry } from '@/lib/api/marketing-report-service';
import {
  ResponsiveContainer, ComposedChart, Bar, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

interface Props { daily: MarketingDailyEntry[]; }

/** Engaged-sessions line — deliberately a fixed dark blue rather than the theme
 *  accent, so it stays distinguishable from the session bars in both themes. */
const ENGAGED_BLUE = '#1e40af';
/** Leads points — emerald rather than a pure green: it shares the blue family's cool
 *  cast so it sits with the session bars and the dark-blue engaged line instead of
 *  clashing, while still being unmistakably a different series. Fixed hex so it holds
 *  up in both themes (green-400 washed out on white). */
const LEADS_GREEN = '#10b981';

/**
 * Daily traffic: sessions (bars), engaged sessions (line), leads (points).
 *
 * Sessions are back as the headline series now that the report queries the GA4 Data
 * API live: each widget asks only for the dimensions it needs, so session counts are
 * exact. They were previously replaced by pageviews because the old stored-aggregate
 * path summed sessions across page_path rows and inflated them (~+25%).
 *
 * Leads (Twenty `website-cta`) sit on a SECOND axis and are drawn as points with no
 * connecting line: daily counts are single digits against hundreds of sessions, so a
 * shared scale would flatten them, and a line would imply continuity between sparse
 * events that isn't there. Zero-lead days are passed as null rather than 0 so no point
 * is drawn at all — otherwise a run of dots along the baseline reads as data.
 */
export function MarketingDailyChart({ daily }: Props) {
  if (daily.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle>Daily Traffic</CardTitle></CardHeader>
        <CardContent><p className="text-sm text-muted-foreground">No data for this range.</p></CardContent>
      </Card>
    );
  }

  const data = daily.map(d => ({
    date: d.date.slice(5), // MM-DD
    Sessions: d.sessions,
    Engaged: d.engagedSessions,
    // null rather than 0: Recharts skips null points, so zero-lead days render nothing
    Leads: d.leads ? d.leads : null,
  }));

  const totalLeads = data.reduce((s, d) => s + (d.Leads ?? 0), 0);
  // Keep the leads axis readable when volume is low: never scale below 0–4.
  const maxLeads = Math.max(4, ...data.map(d => d.Leads ?? 0));

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Daily Traffic
          <span className="ml-2 text-sm font-normal text-muted-foreground">
            {totalLeads} lead{totalLeads !== 1 ? 's' : ''} in range
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={data} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis
              yAxisId="sessions"
              allowDecimals={false}
              tick={{ fontSize: 11 }}
              label={{ value: 'Sessions', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
            />
            <YAxis
              yAxisId="leads"
              orientation="right"
              allowDecimals={false}
              domain={[0, maxLeads]}
              tick={{ fontSize: 11 }}
              label={{ value: 'Leads', angle: 90, position: 'insideRight', style: { fontSize: 11 } }}
            />
            <Tooltip />
            <Legend />
            <Bar yAxisId="sessions" dataKey="Sessions" fill="hsl(var(--primary) / 0.35)" radius={[2, 2, 0, 0]} />
            <Line
              yAxisId="sessions"
              type="monotone"
              dataKey="Engaged"
              stroke={ENGAGED_BLUE}
              strokeWidth={2}
              dot={false}
            />
            {/* Points only — no connecting line between sparse lead events.
                stroke carries the colour Recharts uses for the legend swatch, so it
                must be set even though no line is drawn; strokeWidth={0} is what
                actually suppresses the line. stroke="none" here left the legend
                entry colourless — a labelled gap with no dot. */}
            <Line
              yAxisId="leads"
              dataKey="Leads"
              stroke={LEADS_GREEN}
              strokeWidth={0}
              dot={{ r: 4, fill: LEADS_GREEN, stroke: LEADS_GREEN }}
              activeDot={{ r: 5 }}
              legendType="circle"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
